import {
  CRM_STATUS_ORDER,
  crmOptionsSchema,
  optionLabels,
  type CrmAccountStatus,
  type CrmOptions,
} from "@shared/modules/crm";
import { moduleOptions } from "../../../core/blueprint";
import { toDate } from "../../../core/lib/format";

export {
  CRM_STATUS_ORDER,
  CRM_STATUS_LABELS,
  CRM_SOURCE_LABELS,
  CRM_TASK_TYPE_LABELS,
  CRM_TASK_STATUS_LABELS,
  CRM_PRIORITY_LABELS,
  CRM_MEETING_KIND_LABELS,
  CRM_MEETING_STATUS_LABELS,
  CRM_STAGE_LABELS,
  CRM_REVENUE_KIND_LABELS,
  CRM_REVENUE_STATUS_LABELS,
  CRM_ACTIVITY_LABELS,
} from "@shared/modules/crm";

type BadgeVariant = "neutral" | "primary" | "success" | "warning" | "danger" | "live";

/**
 * Options du module telles que le build les a reçues de blueprint.json, défauts
 * appliqués par le schéma partagé (le blueprint ne transporte que ce qui a été
 * surchargé).
 */
export const CRM_OPTIONS: CrmOptions = crmOptionsSchema.parse(moduleOptions("crm", {}));

/** Libellés des secteurs et partenaires configurés pour ce projet. */
export const CRM_SECTOR_LABELS: Record<string, string> = optionLabels(CRM_OPTIONS.sectors);
export const CRM_PARTNER_LABELS: Record<string, string> = optionLabels(CRM_OPTIONS.partners);

/** Libellé d'un code de secteur / partenaire, le code brut si la liste a changé depuis. */
export function sectorLabel(value: string): string {
  return CRM_SECTOR_LABELS[value] ?? value;
}

export function partnerLabel(value: string): string {
  return CRM_PARTNER_LABELS[value] ?? value;
}

/** Variante de Badge par statut. */
export const CRM_STATUS_VARIANT: Record<CrmAccountStatus, BadgeVariant> = {
  prospect: "neutral",
  en_cours: "warning",
  active: "primary",
  en_service: "success",
  suspendu: "warning",
  perdu: "danger",
};

/** Formatage monétaire fr-CA. 123456 -> "1 234,56 $". */
export function formatCents(cents: number, currency: string = CRM_OPTIONS.currency): string {
  try {
    return new Intl.NumberFormat("fr-CA", { style: "currency", currency }).format(cents / 100);
  } catch {
    // Intl lève sur un code hors ISO 4217 : une ligne héritée d'un import ne doit
    // pas faire écran blanc, elle s'affiche avec son code de devise brut.
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

/** Saisie utilisateur -> cents. Accepte "1 234,56", "1234.56", "1,234.56". null si vide/invalide. */
export function parseAmountToCents(input: string): number | null {
  // \s couvre les espaces fines et insécables produites par le formatage fr-CA.
  const cleaned = input.replace(/(CAD|USD|EUR)/gi, "").replace(/[\s$€]/g, "");
  if (!cleaned) return null;

  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");
  let normalized: string;
  if (lastComma >= 0 && lastDot >= 0) {
    // Les deux séparateurs présents : le dernier rencontré porte les décimales.
    const decimal = lastComma > lastDot ? "," : ".";
    const thousands = decimal === "," ? "." : ",";
    normalized = cleaned.split(thousands).join("").replace(decimal, ".");
  } else if (lastComma >= 0) {
    // "1,23" est un montant fr-CA, "1,234" un séparateur de milliers anglais.
    const decimals = cleaned.length - lastComma - 1;
    normalized =
      decimals > 0 && decimals <= 2 ? cleaned.replace(",", ".") : cleaned.split(",").join("");
  } else {
    normalized = cleaned;
  }

  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null;
  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;
  const magnitude = Math.round(Math.abs(value) * 100);
  return value < 0 ? -magnitude : magnitude;
}

/** cents -> valeur d'input texte éditable ("1234.56"), "" si 0. */
export function centsToInput(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || cents === 0) return "";
  return (cents / 100).toFixed(2);
}

/** true si la date est passée. Utilise toDate() du noyau. */
export function isOverdue(due: string | number | null | undefined): boolean {
  const d = toDate(due);
  return d !== null && d.getTime() < Date.now();
}

/** "dans 3 jours" / "il y a 2 jours" / "aujourd'hui" — Intl.RelativeTimeFormat fr-CA. */
export function formatRelativeDay(value: string | number | null | undefined): string {
  const d = toDate(value);
  if (!d) return "—";
  // Écart en jours calendaires : « demain 8 h » ne doit pas s'afficher « dans 0 jour ».
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOfDay(d) - startOfDay(new Date())) / 86_400_000);
  return new Intl.RelativeTimeFormat("fr-CA", { numeric: "auto" }).format(days, "day");
}

/** Valeur pour <input type="datetime-local"> à partir d'un timestamp API. */
export function toDateTimeLocalValue(value: string | number | null | undefined): string {
  const d = toDate(value);
  if (!d) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Inverse : valeur d'input datetime-local -> ISO à envoyer à l'API (null si vide). */
export function dateTimeLocalToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Options d'un <select> à partir d'une table code → libellé. */
export function toOptions(labels: Record<string, string>): Array<{ value: string; label: string }> {
  return Object.entries(labels).map(([value, label]) => ({ value, label }));
}

/** Comptes qui peuvent porter un dossier : les administrateurs. */
export interface AdminUser {
  id: string;
  name: string;
  role: "admin" | "user";
}
